import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Pressable, Animated, Easing, Platform } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Polygon } from 'react-native-svg';
import { useTheme } from '../../../theme/useTheme';
import type { Bundle, BundleArtKind } from './CoinStoreModal.constants';
import { PALETTE } from './CoinStoreModal.constants';
import { coinStoreStyles } from './CoinStoreModal.styles';
import { IDLE_COIN_SOURCE, getAssetUrl, ASSET_PATHS } from '../../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';
import { WEB_HOVER_TRANSITION } from '../../../shared/constants/animations';

const USE_NATIVE = true;

type CoinStoreStyles = ReturnType<typeof coinStoreStyles>;

function BundleArt({ kind, styles }: { kind: BundleArtKind; styles: CoinStoreStyles }) {
  if (kind === 'one') {
    return (
      <Image source={IDLE_COIN_SOURCE} style={styles.artCoin64}
        alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
    );
  }
  if (kind === 'two') {
    return (
      <View style={styles.artTwoWrap}>
        <Image source={IDLE_COIN_SOURCE} style={styles.artCoinLarge}
          alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
        <Image source={IDLE_COIN_SOURCE} style={styles.artCoinSmallRight}
          alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
      </View>
    );
  }
  if (kind === 'three') {
    return (
      <View style={styles.artThreeWrap}>
        <Image source={IDLE_COIN_SOURCE} style={styles.artCoinSmallLeft}
          alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
        <Image source={IDLE_COIN_SOURCE} style={styles.artCoinCenter}
          alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
        <Image source={IDLE_COIN_SOURCE} style={styles.artCoinSmallRight3}
          alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
      </View>
    );
  }
  if (kind === 'stack') {
    return (
      <Image source={{ uri: getAssetUrl(ASSET_PATHS.COINS.COIN_100) }}
        style={styles.artStack} alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
    );
  }
  if (kind === 'pile-x') {
    return (
      <Image source={{ uri: getAssetUrl(ASSET_PATHS.COINS.COIN_150) }}
        style={styles.artPileX} alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
    );
  }
  return (
    <Image source={{ uri: getAssetUrl(ASSET_PATHS.COINS.COIN_150) }}
      style={styles.artPile} alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
  );
}

function buildStarPoints(size: number, points: number, innerRatio: number): string {
  const pts: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? size / 2 : (size / 2) * innerRatio;
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    pts.push(`${size / 2 + r * Math.cos(a)},${size / 2 + r * Math.sin(a)}`);
  }
  return pts.join(' ');
}

function SaleSticker({ save, label, color, styles }: { save: string; label?: string; color: string; styles: CoinStoreStyles }) {
  const wobble = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(Animated.sequence([
      Animated.timing(wobble, { toValue: 1, duration: 1300, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
      Animated.timing(wobble, { toValue: 0, duration: 1300, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
    ]));
    anim.start();
    return () => anim.stop();
  }, [wobble]);
  const rotate = wobble.interpolate({ inputRange: [0, 1], outputRange: ['-6deg', '2deg'] });
  const scale = wobble.interpolate({ inputRange: [0, 1], outputRange: [1, 1.06] });
  const points = buildStarPoints(56, 12, 0.75);
  return (
    <Animated.View style={[styles.stickerWrap, { transform: [{ rotate }, { scale }] }]}>
      <Svg width={56} height={56} style={styles.stickerSvgAbs}>
        <Polygon points={points} fill={color} stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" />
      </Svg>
      <View style={styles.stickerTextRotate}>
        {label ? (
          <Text style={styles.stickerText}>{label}</Text>
        ) : (
          <>
            <Text style={styles.stickerText}>SAVE</Text>
            <Text style={styles.stickerTextBig}>{save}</Text>
          </>
        )}
      </View>
    </Animated.View>
  );
}

interface BundleCardProps {
  bundle: Bundle;
  cardWidth: number;
  onBuy: (bundle: Bundle, ref: React.RefObject<View | null>) => void;
  loading: boolean;
}

export const BundleCard: React.FC<BundleCardProps> = ({ bundle, cardWidth, onBuy, loading }) => {
  const theme = useTheme();
  const styles = useMemo(() => coinStoreStyles(theme), [theme]);
  const cardRef = useRef<View | null>(null);
  const sparkOps = useRef([
    new Animated.Value(0), new Animated.Value(0), new Animated.Value(0),
  ]).current;
  const shimmerX = useRef(new Animated.Value(-80)).current;

  useEffect(() => {
    const delays = [0, 700, 1300];
    const anims = sparkOps.map((op, i) =>
      Animated.loop(Animated.sequence([
        Animated.delay(delays[i]),
        Animated.timing(op, { toValue: 1, duration: 500, useNativeDriver: USE_NATIVE }),
        Animated.timing(op, { toValue: 0, duration: 500, useNativeDriver: USE_NATIVE }),
        Animated.delay(1200),
      ]))
    );
    anims.forEach((a) => a.start());
    return () => anims.forEach((a) => a.stop());
  }, [sparkOps]);

  useEffect(() => {
    if (!bundle.best) return;
    const anim = Animated.loop(Animated.sequence([
      Animated.timing(shimmerX, { toValue: cardWidth + 80, duration: 1800, useNativeDriver: USE_NATIVE }),
      Animated.delay(1400),
      Animated.timing(shimmerX, { toValue: -80, duration: 0, useNativeDriver: USE_NATIVE }),
    ]));
    anim.start();
    return () => anim.stop();
  }, [bundle.best, shimmerX, cardWidth]);

  const stickerColor = bundle.best ? theme.accent : bundle.popular ? theme.primary : theme.error;
  const stickerLabel = bundle.best ? 'BEST\nVALUE' : bundle.popular ? 'POPULAR' : undefined;

  const gradTop = PALETTE.violetLight;
  const gradMid = PALETTE.violetMid;
  const gradBot = PALETTE.violetDark;

  const handlePress = useCallback(() => {
    onBuy(bundle, cardRef);
  }, [bundle, onBuy]);

  return (
    <Pressable
      ref={cardRef}
      collapsable={false}
      style={({ pressed, hovered }) => [
        styles.bundleCard,
        bundle.best && styles.bundleCardBest,
        { width: cardWidth },
        Platform.select({ web: { transition: WEB_HOVER_TRANSITION } as never }),
        pressed && styles.bundleCardPressed,
        hovered && Platform.OS === 'web' && styles.bundleCardHover,
      ]}
      onPress={handlePress}
      disabled={loading}
      accessibilityRole="button"
      accessibilityLabel={`Buy ${bundle.coins} coins for ${bundle.price}`}
    >
      <LinearGradient
        colors={[gradTop, gradMid, gradBot]}
        style={styles.bundleGradientFill}
      />

      {bundle.best && (
        <View style={styles.bundleShimmerClip}>
          <Animated.View style={[styles.bundleShimmerStrip, { transform: [{ translateX: shimmerX }] }]}>
            <LinearGradient
              colors={['transparent', 'rgba(255,255,255,0.35)', 'transparent']}
              start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }}
              style={styles.bannerShimmerGradient}
            />
          </Animated.View>
        </View>
      )}

      <SaleSticker save={bundle.save} label={stickerLabel} color={stickerColor} styles={styles} />

      <Text style={styles.bundleCount}>{bundle.coins.toLocaleString()}</Text>

      <View style={styles.bundleArtWrap}>
        <BundleArt kind={bundle.art} styles={styles} />
        {sparkOps.map((op, i) => (
          <Animated.View key={i} style={[
            i === 0 ? styles.sparkle0 : i === 1 ? styles.sparkle1 : styles.sparkle2,
            { opacity: op },
          ]}>
            <Text style={i === 0 ? styles.sparkleTxtLg : styles.sparkleTxtSm}>✦</Text>
          </Animated.View>
        ))}
      </View>

      <View style={styles.bundlePriceRow}>
        <Text style={styles.bundleOldPrice}>{bundle.oldPrice}</Text>
        <LinearGradient
          colors={[PALETTE.buyGreen, PALETTE.buyGreenDark]}
          style={[styles.bundleBuyBtn, loading && styles.bundleBuyBtnLoading]}
        >
          <Text style={styles.bundleBuyBtnText}>{bundle.price}</Text>
        </LinearGradient>
      </View>
    </Pressable>
  );
};
